import { z } from 'zod';
/**
 * Painel do dono: agregados do negócio (contas, planos, receita, custos, uso,
 * segurança). Só a conta do dono recebe; nunca traz conteúdo privado de ninguém.
 */
export const donoKpiSchema = z.object({
    ativos30d: z.number().int(),
    novosMes: z.number().int(),
    pagantes: z.number().int(),
    porPlano: z.record(z.string(), z.number().int()),
    /** Receita mensal recorrente estimada, em centavos de real. */
    mrrCents: z.number().int(),
    custoIaMesCents: z.number().int(),
    fixosMesCents: z.number().int(),
    resultadoMesCents: z.number().int(),
    cancelamentosMes: z.number().int(),
    emTeste: z.number().int(),
    testeParaPago60d: z.object({ testes: z.number().int(), pagos: z.number().int() }),
    usdBrl: z.number(),
});
export const donoPessoaSchema = z.object({
    id: z.uuid(),
    username: z.string(),
    displayName: z.string(),
    createdAt: z.iso.datetime(),
    plan: z.string(),
    trialUntil: z.iso.datetime().nullable(),
    effectiveUntil: z.iso.datetime().nullable(),
    lastSeenAt: z.iso.datetime().nullable(),
    devices: z.number().int(),
    platform: z.string().nullable(),
    gastoIa30dCents: z.number().int(),
    /** Número da conta na ordem de cadastro (1 = a primeira). */
    numero: z.number().int().default(0),
    /** Onde a conta nasceu: o primeiro aparelho (site, app da loja, app de teste, iPhone). */
    origem: z.string().default(''),
});
export const donoDinheiroSchema = z.object({
    canais: z.array(z.object({ canal: z.string(), assinantes: z.number().int(), mrrCents: z.number().int() })),
    renovacoes30d: z.number().int(),
    inadimplentes: z.number().int(),
    custoIaMeses: z.array(z.object({ mes: z.string(), cents: z.number().int() })),
    mrrPorPlano: z.array(z.object({ plan: z.string(), assinantes: z.number().int(), mrrCents: z.number().int() })),
});
export const donoUsoSchema = z.object({
    eventos30d: z.array(z.object({ nome: z.string(), total: z.number().int() })),
    ferramentas30d: z.array(z.object({ nome: z.string(), total: z.number().int() })),
    vozMinutos30d: z.number().int(),
    vozSessoes30d: z.number().int(),
    metricas30d: z.array(z.object({ metrica: z.string(), total: z.number().int() })),
    /** "Quero o Sec na minha cidade": quantas pessoas pediram cada cidade, a mais pedida antes. */
    cidades: z
        .array(z.object({ cidade: z.string(), total: z.number().int(), ultimo: z.string() }))
        .default([]),
});
export const donoCustoFixoSchema = z.object({
    id: z.uuid(),
    label: z.string().max(80),
    vendor: z.string().max(80),
    amountCents: z.number().int(),
    /** mensal ou anual (o painel divide por 12). */
    recurrence: z.enum(['mensal', 'anual']),
    active: z.boolean(),
    createdAt: z.iso.datetime(),
});
export const donoCustoFixoInputSchema = z.object({
    label: z.string().trim().min(1).max(80),
    vendor: z.string().trim().max(80).default(''),
    amountCents: z.number().int().min(0).max(100_000_000),
    recurrence: z.enum(['mensal', 'anual']).default('mensal'),
});
export const donoCustosSchema = z.object({
    porModelo30d: z.array(z.object({
        modelo: z.string(),
        finalidade: z.string(),
        pedidos: z.number().int(),
        cents: z.number().int(),
    })),
    porPessoa30d: z.array(z.object({ username: z.string(), cents: z.number().int(), pedidos: z.number().int() })),
    fixos: z.array(donoCustoFixoSchema),
    margemPorPlano: z.array(z.object({
        plan: z.string(),
        assinantes: z.number().int(),
        receitaCents: z.number().int(),
        custoIaCents: z.number().int(),
    })),
});
export const donoSuporteSchema = z.object({
    conversas30d: z.number().int(),
    noApp30d: z.number().int(),
    naCapa30d: z.number().int(),
    passaram30d: z.number().int(),
    perguntasFrequentes: z.array(z.object({ pergunta: z.string(), total: z.number().int() })),
    telas: z.array(z.object({ tela: z.string(), total: z.number().int() })),
    ultimas: z.array(z.object({
        quando: z.iso.datetime(),
        publico: z.boolean(),
        pergunta: z.string(),
        resposta: z.string(),
        passou: z.boolean(),
    })),
});
export const donoSegurancaSchema = z.object({
    eventos7d: z.array(z.object({ tipo: z.string(), total: z.number().int(), falhas: z.number().int() })),
    aparelhosNovos7d: z.number().int(),
    sessoesAtivas: z.number().int(),
    fatores: z.array(z.object({ kind: z.string(), total: z.number().int() })),
});
/** CREF em uso (estúdios e fichas assinadas) e o que o dono conferiu no site do conselho. */
export const DONO_CREF_STATUSES = ['pendente', 'conferido', 'nao_encontrado'];
export const donoCrefSchema = z.object({
    registration: z.string(),
    status: z.enum(DONO_CREF_STATUSES),
    checkedAt: z.string().nullable(),
    /** Onde aparece: o estúdio e quem assinou. */
    uses: z.array(z.object({ clinic: z.string(), by: z.string().nullable() })),
});
export const donoCrefMarkSchema = z.object({
    registration: z.string().min(4).max(40),
    status: z.enum(['conferido', 'nao_encontrado', 'pendente']),
});
/** Sala do dono (G2): as diretorias que organizam o painel. */
export const DONO_DIRETORIAS = [
    'visao',
    'produto',
    'crescimento',
    'financas',
    'operacoes',
    'seguranca',
    'atendimento',
    'conformidade',
];
/** O "Hoje" da sala: números do negócio, saúde da máquina e o que precisa do dono. */
export const donoHojeSchema = z.object({
    geradoEm: z.iso.datetime(),
    release: z.string().nullable(),
    numeros: z.object({
        ativos30d: z.number().int(),
        novosHoje: z.number().int(),
        pagantesPessoal: z.number().int(),
        pagantesNegocio: z.number().int(),
        /** Pessoal e negócio juntos, estimado pelo catálogo. */
        mrrCents: z.number().int(),
        mrrNegocioCents: z.number().int(),
        custoIaMesCents: z.number().int(),
        fixosMesCents: z.number().int(),
        resultadoMesCents: z.number().int(),
        custoIaPorAtivoCents: z.number().int(),
    }),
    maquina: z.object({
        discoPct: z.number().int().nullable(),
        discoLivreGb: z.number().int().nullable(),
        memoriaPct: z.number().int(),
        carga1m: z.number(),
        cpus: z.number().int(),
        ligadaHa: z.number().int(),
    }),
    alertas: z.array(z.object({
        nivel: z.enum(['urgente', 'atencao']),
        diretoria: z.enum(DONO_DIRETORIAS),
        titulo: z.string(),
        detalhe: z.string(),
    })),
});
